/**
 * Checklists Dual-Write Test
 *
 * Tests that checklist operations in Firestore are properly synced to Supabase.
 *
 * Test variations:
 * CL1: Create checklist (add)
 * CL2: Update checklist content
 */

import {
  addChecklist,
  updateChecklist,
} from '../global/Firebase/firebase';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, storesDictAtom, dbAtom, organizationIdAtom } from '../global/AtomsProvider';

// Test variation definitions
export const CHECKLISTS_TEST_VARIATIONS = [
  { id: 'CL1', category: 'create', name: 'Create checklist' },
  { id: 'CL2', category: 'update', name: 'Update checklist content' },
];

// Get test categories for UI grouping
export function getChecklistsTestCategories() {
  return [
    { id: 'create', name: 'Create Checklist', variations: ['CL1'] },
    { id: 'update', name: 'Update Checklist', variations: ['CL2'] },
  ];
}

// Helper to normalize values for comparison
function normalizeValue(val) {
  if (val === undefined || val === null) return null;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
    return val.trim();
  }
  return val;
}

// Helper to compare values with tolerance for floating point
function valuesMatch(a, b, tolerance = 0.01) {
  const normA = normalizeValue(a);
  const normB = normalizeValue(b);

  if (normA === normB) return true;
  if (normA === null || normB === null) return normA === normB;

  // Numeric comparison with tolerance
  if (typeof normA === 'number' && typeof normB === 'number') {
    return Math.abs(normA - normB) < tolerance;
  }

  return normA === normB;
}

/**
 * Get test data based on atoms
 */
export function getChecklistsTestData() {
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const db = jotaiStore.get(dbAtom);
  const organizationId = jotaiStore.get(organizationIdAtom);

  if (!organization || !db) {
    return null;
  }

  // Get first store
  const storeEntries = Object.entries(storesDict || {});
  if (storeEntries.length < 1) {
    return null;
  }

  const store = { id: storeEntries[0][0], ...storeEntries[0][1] };

  return {
    organization,
    store,
    db,
    organizationId,
  };
}

/**
 * Generate a checklist ID in the expected format: YYYY-MM-DD-storeId-seller
 */
function generateChecklistId(storeId) {
  const today = new Date();
  const dateStr = today.toISOString().split('T')[0];
  // Add random suffix to avoid conflicts
  const random = Math.random().toString(36).substring(2, 8);
  return `${dateStr}-${storeId}-seller-test-${random}`;
}

/**
 * Compare checklist via backend API
 */
async function compareChecklistViaApi(checklistId, checklistData, log) {
  try {
    const response = await api.get(`/checklists/compare/${checklistId}`);
    const apiResult = response.data;

    if (!apiResult.exists) {
      return {
        checklistId,
        foundInSupabase: false,
        matches: false,
        differences: [{
          field: 'checklist',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare checklist_type
    if (checklistData.checklistType) {
      const sbType = normalizeValue(supabaseData?.checklist_type);
      const fsType = normalizeValue(checklistData.checklistType);
      if (!valuesMatch(sbType, fsType)) {
        differences.push({
          field: 'checklist_type',
          supabaseValue: sbType,
          firestoreValue: fsType,
          severity: 'critical',
        });
      }
    }

    // Compare content (lenient - just check if it exists and has similar structure)
    const sbContentLength = Array.isArray(supabaseData?.content) ? supabaseData.content.length : 0;
    const fsContentLength = Array.isArray(checklistData.content) ? checklistData.content.length : 0;
    if (sbContentLength !== fsContentLength) {
      differences.push({
        field: 'content_length',
        supabaseValue: sbContentLength,
        firestoreValue: fsContentLength,
        severity: 'warning',
      });
    }

    return {
      checklistId,
      foundInSupabase: true,
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
      warnings: differences.filter(d => d.severity === 'warning').map(d => d.field),
      supabaseData,
    };
  } catch (error) {
    log(`API comparison error: ${error.message}`);
    return {
      checklistId,
      foundInSupabase: false,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: null,
        firestoreValue: error.message,
        severity: 'critical',
      }],
      warnings: [`API error: ${error.message}`],
    };
  }
}

/**
 * Delete a checklist from Firestore (cleanup helper)
 * Note: Checklists don't have a dedicated delete function, so we use the db directly
 */
async function deleteChecklistFromFirestore(db, checklistId) {
  try {
    await db.collection('checklists').doc(checklistId).delete();
  } catch (error) {
    console.warn('Failed to delete test checklist:', error.message);
  }
}

/**
 * Run a single checklist test
 */
async function runSingleTest(variationId, testData, log) {
  const { db, store, organizationId } = testData;

  let createdChecklistId = null;
  let result = {
    variationId,
    passed: false,
    error: null,
    comparisonResult: null,
  };

  try {
    switch (variationId) {
      case 'CL1': {
        // Create checklist
        const checklistId = generateChecklistId(store.id);
        const checklistData = {
          store: store.id,
          organizationId,
          checklistType: 'seller',
          date: new Date().toISOString().split('T')[0],
          content: [
            { type: 'task', text: 'Test task 1', done: false },
            { type: 'task', text: 'Test task 2', done: true },
            { type: 'extra', text: 'Extra note', done: false },
          ],
        };

        log(`Creating checklist: ${checklistId}`);
        await addChecklist(db, checklistId, checklistData);
        createdChecklistId = checklistId;

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare
        const comparison = await compareChecklistViaApi(checklistId, checklistData, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test checklist...');
        await deleteChecklistFromFirestore(db, checklistId);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'CL2': {
        // Update checklist test
        const checklistId = generateChecklistId(store.id);
        const checklistData = {
          store: store.id,
          organizationId,
          checklistType: 'seller',
          date: new Date().toISOString().split('T')[0],
          content: [
            { type: 'task', text: 'Original task', done: false },
          ],
        };

        log('Creating checklist to update...');
        await addChecklist(db, checklistId, checklistData);
        createdChecklistId = checklistId;

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await compareChecklistViaApi(checklistId, checklistData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Checklist was not created in Supabase';
          break;
        }

        // Now update via Firestore
        const updatedContent = [
          { type: 'task', text: 'Original task', done: true },
          { type: 'task', text: 'New task added', done: false },
        ];
        const updatedData = {
          ...checklistData,
          content: updatedContent,
        };

        log('Updating checklist content...');
        await updateChecklist(db, checklistId, updatedData);

        log('Waiting 6000ms for update sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare updated values
        const updateCheck = await compareChecklistViaApi(checklistId, updatedData, log);
        result.comparisonResult = updateCheck;

        // Check that content was updated (has 2 items now)
        const supabaseContentLength = Array.isArray(updateCheck.supabaseData?.content)
          ? updateCheck.supabaseData.content.length
          : 0;
        result.passed = updateCheck.foundInSupabase && supabaseContentLength === 2;

        // Cleanup
        log('Cleaning up test checklist...');
        await deleteChecklistFromFirestore(db, createdChecklistId);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      default:
        result.error = `Unknown variation: ${variationId}`;
    }
  } catch (error) {
    result.error = error.message;
    log(`Error: ${error.message}`);

    // Cleanup on error
    if (createdChecklistId) {
      try {
        await deleteChecklistFromFirestore(db, createdChecklistId);
      } catch (cleanupError) {
        log(`Cleanup failed: ${cleanupError.message}`);
      }
    }
  }

  return result;
}

/**
 * Run all checklists tests
 */
export async function runFullChecklistsTest(log = console.log, selectedVariations = null) {
  log('Starting checklists dual-write tests...');

  const testData = getChecklistsTestData();
  if (!testData) {
    log('ERROR: Could not get test data. Need at least 1 store.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  log('\n========================================');
  log('CHECKLISTS DUAL-WRITE TEST SUITE');
  log('========================================');
  log(`Organization: ${testData.organization.name || testData.organizationId}`);
  log(`Store: ${testData.store.name || testData.store.id}`);
  log('========================================\n');

  const variationsToRun = selectedVariations || CHECKLISTS_TEST_VARIATIONS.map(v => v.id);
  const results = [];
  let passed = 0;
  let failed = 0;
  let skipped = 0;

  for (const variationId of variationsToRun) {
    const variation = CHECKLISTS_TEST_VARIATIONS.find(v => v.id === variationId);
    if (!variation) {
      log(`Skipping unknown variation: ${variationId}`);
      skipped++;
      continue;
    }

    log(`\n--- Running test ${variationId}: ${variation.name} ---`);

    const result = await runSingleTest(variationId, testData, log);
    results.push(result);

    if (result.error) {
      log(`ERROR: ${result.error}`);
      failed++;
    } else if (result.passed) {
      log(`PASS: ${variation.name}`);
      passed++;
    } else {
      log(`FAIL: ${variation.name}`);
      if (result.comparisonResult?.differences?.length > 0) {
        log('Differences: ' + JSON.stringify(result.comparisonResult.differences, null, 2));
      }
      failed++;
    }
  }

  log('\n========================================');
  log('TEST SUMMARY');
  log('========================================');
  log(`Passed: ${passed}`);
  log(`Failed: ${failed}`);
  log(`Skipped: ${skipped}`);
  log('========================================');

  if (failed > 0) {
    log('\nFAILED TESTS:');
    results.filter(r => !r.passed && !r.error).forEach(r => {
      const variation = CHECKLISTS_TEST_VARIATIONS.find(v => v.id === r.variationId);
      log(`  - ${r.variationId}: ${variation?.name}`);
      r.comparisonResult?.differences?.forEach(d => {
        log(`    ${d.field}: Supabase="${d.supabaseValue}" vs Firestore="${d.firestoreValue}"`);
      });
    });
  }

  return { passed, failed, skipped, results };
}

export default {
  CHECKLISTS_TEST_VARIATIONS,
  getChecklistsTestCategories,
  getChecklistsTestData,
  runFullChecklistsTest,
};
